import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getYardsByCompanyId, getReportStats } from "../../pages/Home/api";
import { formatDateToISOString } from "../../utils/utils";

const getLocalDateString = (d = new Date()) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultDates = () => {
  const today = new Date();
  const start = new Date();
  start.setDate(today.getDate() - 6);
  return {
    startDate: getLocalDateString(start),
    endDate: getLocalDateString(today),
  };
};

// A persisted endDate older than "today" is stale and must not be trusted.
const isDateStale = (date) => !!date && date < getLocalDateString();

export const fetchHomeData = createAsyncThunk(
  "home/fetchHomeData",
  async ({ companyId, filters }, { rejectWithValue }) => {
    try {
      const res = await getYardsByCompanyId(companyId);
      if (!res.status || !Array.isArray(res.data))
        throw new Error("Failed to fetch yards");

      const yardOptions = res.data.map((yard) => ({
        label: yard.yard_name,
        value: yard.yard_id,
      }));
      const allYardIds = res.data.map((yard) => yard.yard_id);
      const defaultDates = getDefaultDates();

      // Guard against a stale persisted endDate being sent to the API
      // before any correction has a chance to run — applied here, at the
      // point the payload is built, so the very first request is correct.
      const safeStartDate = filters.startDate || defaultDates.startDate;
      const safeEndDate = isDateStale(filters.endDate)
        ? defaultDates.endDate
        : filters.endDate || defaultDates.endDate;

      const payload = {
        yardIds: filters.yards?.length > 0 ? filters.yards : allYardIds,
        startDate: formatDateToISOString(safeStartDate, true),
        endDate: formatDateToISOString(safeEndDate, false),
      };

      const reportData = await getReportStats(payload);

      return {
        yardOptions,
        allYardIds,
        reportStats: reportData?.data || [],
        defaultDates,
        lastFetchedFilters: {
          yards: payload.yardIds,
          startDate: payload.startDate,
          endDate: payload.endDate,
        },
      };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchReportStats = createAsyncThunk(
  "home/fetchReportStats",
  async ({ yardIds, startDate, endDate }, { rejectWithValue }) => {
    try {
      const res = await getReportStats({ yardIds, startDate, endDate });
      return {
        reportStats: res.data,
        lastFetchedFilters: {
          yards: yardIds,
          startDate,
          endDate,
        },
      };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchYardsForCompany = createAsyncThunk(
  "home/fetchYardsForCompany",
  async ({ companyId }, { rejectWithValue }) => {
    try {
      const res = await getYardsByCompanyId(companyId);
      if (!res.status || !Array.isArray(res.data))
        throw new Error("Failed to fetch yards");

      const yardOptions = res.data.map((yard) => ({
        label: yard.yard_name,
        value: yard.yard_id,
      }));
      const allYardIds = res.data.map((yard) => yard.yard_id);

      return { yardOptions, allYardIds };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const homeSlice = createSlice({
  name: "home",
  initialState: {
    yardOptions: [],
    reportStats: [],
    isLoading: false,
    isYardsLoading: false,
    error: null,
    lastFetchedFilters: null,
  },
  reducers: {
    resetHomeStats: (state) => {
      state.reportStats = [];
      state.lastFetchedFilters = null;
    },
    resetYards: (state) => {
      state.yardOptions = [];
      state.reportStats = [];
      state.lastFetchedFilters = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchHomeData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchHomeData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.yardOptions = action.payload.yardOptions;
        state.reportStats = action.payload.reportStats;
        state.lastFetchedFilters = action.payload.lastFetchedFilters;
      })
      .addCase(fetchHomeData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchReportStats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchReportStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.reportStats = action.payload.reportStats;
        state.lastFetchedFilters = action.payload.lastFetchedFilters;
      })
      .addCase(fetchReportStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchYardsForCompany.pending, (state) => {
        state.isYardsLoading = true;
      })
      .addCase(fetchYardsForCompany.fulfilled, (state, action) => {
        state.isYardsLoading = false;
        state.yardOptions = action.payload.yardOptions;
      })
      .addCase(fetchYardsForCompany.rejected, (state, action) => {
        state.isYardsLoading = false;
        state.error = action.payload;
      });
  },
});

export const { resetHomeStats, resetYards } = homeSlice.actions;
export default homeSlice.reducer;